import { isRouteErrorResponse } from "react-router-dom";
import type { TFunction } from "i18next";
import type { ErrorInfo } from "../types";
import {
  ErrorIcon,
  LockIcon,
  ShieldIcon,
  ServerErrorIcon,
  NetworkErrorIcon,
} from "../components/atoms/ErrorIcon";

export function getErrorInfo(error: unknown, t: TFunction): ErrorInfo {
  if (isRouteErrorResponse(error)) {
    return handleRouteErrorResponse(error, t);
  }

  if (isNetworkError(error)) {
    return {
      title: t("errors.boundary.network.title"),
      message: t("errors.boundary.network.message"),
      suggestion: t("errors.boundary.network.suggestion"),
      canRetry: true,
      canGoBack: false,
      errorType: "network-error",
    };
  }

  if (error instanceof Error) {
    return {
      title: t("errors.boundary.unexpected.title"),
      // Em DEV mostramos a `error.message` técnica para ajudar o debug;
      // em produção, mensagem amigável traduzida (a `error.message` crua
      // do back nunca é exibida ao usuário — ver `i18n.mdc` §5).
      message: import.meta.env.DEV
        ? error.message
        : t("errors.boundary.unexpected.message"),
      suggestion: t("errors.boundary.unexpected.suggestion"),
      canRetry: true,
      canGoBack: true,
      errorType: "unknown",
    };
  }

  return {
    title: t("errors.boundary.unknown.title"),
    message: t("errors.boundary.unknown.message"),
    suggestion: t("errors.boundary.unknown.suggestion"),
    canRetry: true,
    canGoBack: true,
    errorType: "unknown",
  };
}

function handleRouteErrorResponse(
  error: {
    status: number;
    statusText: string;
    data?: { message?: string };
  },
  t: TFunction,
): ErrorInfo {
  switch (error.status) {
    case 401:
      return {
        status: 401,
        title: t("errors.boundary.unauthorized.title"),
        message: t("errors.boundary.unauthorized.message"),
        suggestion: t("errors.boundary.unauthorized.suggestion"),
        canRetry: false,
        canGoBack: true,
        errorType: "unauthorized",
      };

    case 403:
      return {
        status: 403,
        title: t("errors.boundary.forbidden.title"),
        message: t("errors.boundary.forbidden.message"),
        suggestion: t("errors.boundary.forbidden.suggestion"),
        canRetry: false,
        canGoBack: true,
        errorType: "forbidden",
      };

    case 404:
      return {
        status: 404,
        title: t("errors.boundary.notFound.title"),
        message: t("errors.boundary.notFound.message"),
        suggestion: t("errors.boundary.notFound.suggestion"),
        canRetry: false,
        canGoBack: true,
        errorType: "not-found",
      };

    case 500:
      return {
        status: 500,
        title: t("errors.boundary.serverError.title"),
        message: t("errors.boundary.serverError.message"),
        suggestion: t("errors.boundary.serverError.suggestion"),
        canRetry: true,
        canGoBack: false,
        errorType: "server-error",
      };

    case 503:
      return {
        status: 503,
        title: t("errors.boundary.serviceUnavailable.title"),
        message: t("errors.boundary.serviceUnavailable.message"),
        suggestion: t("errors.boundary.serviceUnavailable.suggestion"),
        canRetry: true,
        canGoBack: false,
        errorType: "server-error",
      };

    default:
      // `error.statusText` é texto HTTP cru (ex.: "Internal Server Error") e
      // `error.data?.message` pode trazer string técnica do back (ex.:
      // "X.Y.Z = Message" sem lookup i18n). Nenhum dos dois deve ir direto
      // pra UI — viola `i18n.mdc` §5 ("nunca exibir error.message cru") e
      // `error-mapping.mdc` ("a Message do back é genérica, o front sempre
      // traduz via lookup i18n"). Usamos só a string traduzida; o `status`
      // numérico fica disponível em `errorInfo.status` para diagnóstico.
      return {
        status: error.status,
        title: t("errors.boundary.default.title"),
        message: t("errors.boundary.default.message"),
        canRetry: true,
        canGoBack: true,
        errorType: "unknown",
      };
  }
}

function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return (
      error.message.includes("fetch") ||
      error.message.includes("network") ||
      error.message.includes("Failed to fetch")
    );
  }
  return false;
}

export function getErrorIcon(errorType: ErrorInfo["errorType"]) {
  switch (errorType) {
    case "unauthorized":
      return <LockIcon />;
    case "forbidden":
      return <ShieldIcon />;
    case "server-error":
      return <ServerErrorIcon />;
    case "network-error":
      return <NetworkErrorIcon />;
    default:
      return <ErrorIcon />;
  }
}
